import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from "react";
import { AuthContext } from "../Auth/AuthContext";
import useTickets from "../../hooks/useTickets";
import { useHistory } from "react-router-dom";
import useSound from "use-sound";
import alertSound from "../../assets/sound.mp3";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { TicketsContext } from "../Tickets/TicketsContext";

const NotificationsContext = createContext();

const NotificationsProvider = ({ children }) => {
    const { user, socket } = useContext(AuthContext);
    const { setCurrentTicket, currentTicket } = useContext(TicketsContext);
    const [notifications, setNotifications] = useState([]);
    const history = useHistory();
    const { get: getSetting } = useCompanySettings();

    const [showTicketWithoutQueue, setShowTicketWithoutQueue] = useState(false);
    const [showNotificationPending, setShowNotificationPending] = useState(false);
    const [showGroupNotification, setShowGroupNotification] = useState(false);
    const [enableSound, setEnableSound] = useState("enabled");
    const [enablePush, setEnablePush] = useState("enabled");
    const [ignoreGroupMsg, setIgnoreGroupMsg] = useState("disabled");

    const { tickets } = useTickets({ withUnreadMessages: true });
    const [play] = useSound(alertSound);

    const ticketIdUrl = +history.location.pathname.split("/")[2];
    const ticketIdRef = useRef(ticketIdUrl);
    const notifiedMessages = useRef(new Set());

    useEffect(() => {
        ticketIdRef.current = ticketIdUrl;
    }, [ticketIdUrl]);

    // isMounted flag to avoid state updates on unmounted component
    const isMounted = useRef(true);
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    // ── Helper functions (stable references via user) ──────────────────

    const getAllowedWhatsappIds = useCallback(() => {
        const depts = Array.isArray(user.departments) ? user.departments : [];
        if (depts.length > 0) {
            return Array.from(
                new Set(
                    depts.flatMap(d => Array.isArray(d.whatsapps) ? d.whatsapps.map(w => Number(w.id)) : [])
                )
            ).map(Number).filter(Number.isFinite);
        }
        return [
            ...(Array.isArray(user.whatsappIds) ? user.whatsappIds : []),
            ...(user.whatsapp_id ? [Number(user.whatsapp_id)] : [])
        ]
            .map(Number)
            .filter((id, idx, arr) => Number.isFinite(id) && arr.indexOf(id) === idx);
    }, [user.departments, user.whatsappIds, user.whatsapp_id]);

    const getScopedQueueIds = useCallback(() => {
        const depts = Array.isArray(user.departments) ? user.departments : [];
        if (depts.length > 0) {
            return Array.from(
                new Set(
                    depts.flatMap(d => Array.isArray(d.queues) ? d.queues.map(q => Number(q.id)) : [])
                )
            ).map(Number).filter(Number.isFinite);
        }
        return Array.from(
            new Set(
                (Array.isArray(user.queues) ? user.queues : [])
                    .map(q => Number(q?.id))
                    .filter(Number.isFinite)
            )
        );
    }, [user.departments, user.queues]);

    const hasConnectionAccess = useCallback((ticket) => {
        if (!ticket) return false;
        const ticketWhatsappId = Number(ticket.whatsapp_id || ticket.whatsappId);
        
        const allowedWhatsappIds = getAllowedWhatsappIds();
        
        const isUser = user.profile === "user";
        const shouldRestrictWhatsapp = allowedWhatsappIds.length > 0 || isUser;

        if (shouldRestrictWhatsapp) {
            if (allowedWhatsappIds.length > 0) {
                return !!(ticketWhatsappId && allowedWhatsappIds.includes(ticketWhatsappId));
            }
            return false;
        }
        return true;
    }, [getAllowedWhatsappIds, user.profile]);

    const hasQueueAccess = useCallback((ticket) => {
        if (!ticket) return false;
        if (user.profile === "admin" || user.profile === "super_admin") return true;

        const queueId = Number(ticket.queue_id || ticket.queueId);
        const hasQueue = Number.isFinite(queueId) && queueId > 0;
        const scopedQueueIds = getScopedQueueIds();
        // Especialista = tem filas no perfil; Generalista = sem filas (atende fluxo geral)
        const isSpecialist = scopedQueueIds.length > 0;

        if (!hasQueue) {
            // Ticket SEM fila:
            // Especialista NUNCA vê ticket sem fila — só vê tickets na(s) sua(s) fila(s).
            // Generalista vê todos os tickets sem fila (é o canal principal dele).
            return !isSpecialist;
        }

        // Ticket EM fila específica → apenas quem tem aquela fila no perfil
        return scopedQueueIds.includes(queueId);
    }, [getScopedQueueIds, user?.id, user.profile]);

    const hasAssignmentAccess = useCallback((ticket) => {
        if (!ticket) return false;
        if (user.profile === "admin" || user.profile === "super_admin") return true;

        const currentUserId = Number(user.id);
        const ticketUserId = Number(ticket.user_id || ticket.userId);
        
        // Removed ticketOwnerUserId to prevent leakage of transferred tickets
        const isAssigned = ticketUserId === currentUserId;

        const depts = Array.isArray(user.departments) ? user.departments : [];
        const hasSharedVisibility = depts.length > 0 
            ? depts.some(d => String(d.visibilityType || "").toLowerCase() === "shared")
            : false;

        if (user.profile === "supervisor") {
            const ticketSupervisorId = Number(ticket?.user?.supervisorId || ticket?.userSupervisorId);
            const isTeamMember = ticketSupervisorId === currentUserId;
            if (isAssigned || isTeamMember) return true;
            if (ticket.status === "pending") return true;
            return false;
        }

        // Atendente comum
        if (hasSharedVisibility) {
            if (isAssigned) return true;
            if (ticket.status === "pending" && !ticketUserId) return true;
            return false;
        }

        return isAssigned;
    }, [user.departments, user.id, user.profile]);

    // ── useEffect: sync visible tickets ────────────────────────────────

    useEffect(() => {
        if (!user?.id) {
            setNotifications([]);
            return;
        }

        const visibleTickets = (Array.isArray(tickets) ? tickets : []).filter(ticket =>
            hasConnectionAccess(ticket) &&
            hasQueueAccess(ticket) &&
            hasAssignmentAccess(ticket) &&
            !((ticket?.is_group || ticket?.isGroup) && ignoreGroupMsg === "enabled")
        );

        setNotifications(visibleTickets);
    }, [hasAssignmentAccess, hasConnectionAccess, hasQueueAccess, ignoreGroupMsg, tickets, user?.id]);

    // ── useEffect: fetch company settings ──────────────────────────────

    useEffect(() => {
        const fetchSettings = async () => {
            if (!user.id) return;
            const token = localStorage.getItem("token");
            if (!token || token === "undefined" || token === "null") return;
            try {
                const pendingSetting = await getSetting({ column: "showNotificationPending" });
                if (pendingSetting.showNotificationPending === true) setShowNotificationPending(true);

                if (user.allTicket === "enable") setShowTicketWithoutQueue(true);
                if (user.allow_group === true) setShowGroupNotification(true);

                const soundSetting = await getSetting({ column: "enableSound" });
                if (soundSetting.enableSound) setEnableSound(soundSetting.enableSound);

                const pushSetting = await getSetting({ column: "enableBrowserNotifications" });
                if (pushSetting.enableBrowserNotifications) setEnablePush(pushSetting.enableBrowserNotifications);

                const groupSetting = await getSetting({ column: "ignoreGroupMsg" });
                if (groupSetting.ignoreGroupMsg) setIgnoreGroupMsg(groupSetting.ignoreGroupMsg);
            } catch (err) {
                if (err?.response?.status === 401 || err?.response?.status === 403) return;
                console.error(err);
            }
        };
        fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, user?.allTicket, user?.allow_group, getSetting]);

    // ── useEffect: sync settings in real-time via socket ───────────────

    useEffect(() => {
        if (!socket || !user?.company_id) return;

        const company_id = user.company_id;

        const onSettingsChange = (data) => {
            if (data.action === "update" && data.setting) {
                const { key, value } = data.setting;
                if (key === "enableSound") setEnableSound(value);
                if (key === "enableBrowserNotifications") setEnablePush(value);
                if (key === "ignoreGroupMsg") setIgnoreGroupMsg(value);
            }
        };

        socket.on(`company-${company_id}-settings`, onSettingsChange);

        return () => {
            socket.off(`company-${company_id}-settings`, onSettingsChange);
        };
    }, [socket, user?.company_id]);


    // ── handleNotifications: toast + browser push ──────────────────────

    const handleNotifications = useCallback((data) => {
        const { message, contact, ticket } = data;
        if (!message || !ticket) return;

        if (message.id) {
            if (notifiedMessages.current.has(message.id)) return;
            notifiedMessages.current.add(message.id);
            if (notifiedMessages.current.size > 500) {
                const firstValue = notifiedMessages.current.values().next().value;
                notifiedMessages.current.delete(firstValue);
            }
        }

        const shouldPlayAudio = () => {
            if (!user?.audioNotificationWhatsappIds || user.audioNotificationWhatsappIds.length === 0) {
                return true;
            }
            const tId = Number(ticket.whatsapp_id || ticket.whatsappId);
            return user.audioNotificationWhatsappIds.includes(tId) || user.audioNotificationWhatsappIds.map(String).includes(String(tId));
        };

        if (enableSound === "enabled" && shouldPlayAudio()) {
            play();
        }

        const options = {
            body: `${message.body || ""}`,
            icon: contact?.urlPicture || undefined,
            tag: String(ticket.id),
            renotify: true,
        };

        if (enablePush === "enabled" && Notification.permission === "granted") {
            const notification = new Notification(
                `${i18n.t("tickets.notification.message")} ${contact.name}`,
                options
            );

            notification.onclick = (e) => {
                e.preventDefault();
                window.focus();
                history.push(`/tickets/${ticket.id}`);
            };
        }
    }, [enablePush, enableSound, history, play, user?.audioNotificationWhatsappIds]);

    // ── useEffect: socket listeners ────────────────────────────────────

    useEffect(() => {
        if (!socket || !user?.id) return;

        const company_id = user.company_id;

        const onCompanyTicket = (data) => {
            const isDismiss =
                data.action === "updateUnread" ||
                data.action === "delete";

            if (isDismiss) {
                const ticket_id = data.ticket_id || data.ticket?.id;
                setNotifications(prevState => prevState.filter(t => t.id !== ticket_id));
            } else if (data.action === "update" && data.ticket) {
                const unread = data.ticket.unread_messages !== undefined ? data.ticket.unread_messages : data.ticket.unreadMessages;
                const ignoreThisGroup =
                    (data.ticket?.is_group || data.ticket?.isGroup) && ignoreGroupMsg === "enabled";
                if (!hasConnectionAccess(data.ticket) || !hasQueueAccess(data.ticket) || !hasAssignmentAccess(data.ticket) || !unread || unread <= 0 || data.ticket.status === "closed" || ignoreThisGroup) {
                    setNotifications(prevState => prevState.filter(t => t.id !== data.ticket.id));
                    return;
                }
                setNotifications(prevState => {
                    const ticketIndex = prevState.findIndex(t => t.id === data.ticket.id);
                    if (ticketIndex !== -1) {
                        const newList = [...prevState];
                        newList[ticketIndex] = data.ticket;
                        return newList;
                    }

                    if (unread > 0) {
                        return [data.ticket, ...prevState];
                    }

                    return prevState;
                });
            }
        };

        const onCompanyAppMessage = (data) => {
        if (!data || !data.ticket || !data.ticket.id) return;

        // Pré-filtro para evitar requisições GET desnecessárias que geram 403 e sobrecarregam a API
        const preTicket = {
            ...data.ticket,
            whatsapp_id: data.ticket.whatsapp_id !== undefined ? data.ticket.whatsapp_id : data.ticket.whatsappId,
            queue_id: data.ticket.queue_id !== undefined ? data.ticket.queue_id : data.ticket.queueId,
            user_id: data.ticket.user_id !== undefined ? data.ticket.user_id : data.ticket.userId,
        };

        if (!hasConnectionAccess(preTicket) || !hasQueueAccess(preTicket)) {
            return;
        }

        if (!data.ticket || !data.ticket.id || data.ticket.id === "undefined" || data.ticket.id === "null") {
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;
        // Fetch ticket details
        api.get(`/tickets/${data.ticket.id}`, { params: { spy: "true" }, signal })
            .then(res => {
                if (!isMounted.current) return;
                const ticket = res.data;
                const isAuthorized =
                    ticket &&
                    hasConnectionAccess(ticket) &&
                    hasQueueAccess(ticket) &&
                    hasAssignmentAccess(ticket);

                const unread = ticket.unread_messages !== undefined ? ticket.unread_messages : ticket.unreadMessages;
                if (!isAuthorized || !unread || unread <= 0 || ticket.status === "closed") {
                    setNotifications(prevState => prevState.filter(t => t.id !== ticket.id));
                    return;
                }

                // Ignorar Grupos: não entra na lista/badge de notificações.
                if ((ticket?.is_group || ticket?.isGroup) && ignoreGroupMsg === "enabled") {
                    setNotifications(prevState => prevState.filter(t => t.id !== ticket.id));
                    return;
                }

                if (
                    (data.action === "create" || data.action === "update") &&
                    data.message &&
                    !data.message.from_me
                ) {
                    setNotifications(prevState => {
                        const ticketIndex = prevState.findIndex(t => t.id === ticket.id);
                        if (ticketIndex !== -1) {
                            const newList = [...prevState];
                            newList[ticketIndex] = ticket;
                            return newList;
                        }
                        return [ticket, ...prevState];
                    });

                    // Sync current ticket if open
                    if (window.location.pathname === `/tickets/${ticket.id}`) {
                        setCurrentTicket(ticket);
                    }

                    const shouldNotNotificate =
                        (ticket?.user_id && ticket?.user_id !== user?.id) ||
                        (ticket?.is_group && ticket?.whatsapp?.group_as_ticket === "disabled" && !showGroupNotification) ||
                        (ticket?.is_group && ignoreGroupMsg === "enabled");

                    if (!shouldNotNotificate && data.action === "create") {
                        handleNotifications({ ...data, ticket });
                    }
                }
            })
            .catch(err => {
                if (
                    err.name !== 'AbortError' &&
                    err?.response?.status !== 401 &&
                    err?.response?.status !== 403
                ) {
                    console.error(err);
                }
            });
        // Cleanup: abort request if component unmounts
        return () => controller.abort();
    };

        socket.on(`company-${company_id}-ticket`, onCompanyTicket);
        socket.on(`company-${company_id}-appMessage`, onCompanyAppMessage);

        return () => {
            socket.off(`company-${company_id}-ticket`, onCompanyTicket);
            socket.off(`company-${company_id}-appMessage`, onCompanyAppMessage);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleNotifications, hasAssignmentAccess, hasConnectionAccess, hasQueueAccess, ignoreGroupMsg, setCurrentTicket, showGroupNotification, socket, user?.id, user?.company_id]);

    const clearNotification = useCallback((ticketId) => {
        setNotifications(prevState => prevState.filter(t => t.id !== ticketId));
    }, []);

    return (
        <NotificationsContext.Provider value={{ notifications, clearNotification }}>
            {children}
        </NotificationsContext.Provider>
    );
};

export { NotificationsContext, NotificationsProvider };
